import React from "react";
import { Routes as RouterRoutes, Route, Outlet } from "react-router-dom";
import Login from "../auth/Login";
import VAlert from "../layout/Alert";
import { BaseRoute } from "./PrivateRoute";
import Dashboard from "../dashboard/Dashboard";
import PermissionDenied from "./PermissionDenied";
import { ProgrammesScreen } from "../programme/screens/ProgrammesScreen";
import { LayoutWithNav, LayoutWithoutNav } from "../layout/BaseLayout";
import { CreateOrganisationPage } from "../../pages/createOrganisationPage";
import { ComponentLibrary } from "../layout/ComponentLibrary";
import { UsersScreen } from "../users/UsersScreen";
import { SessionModeScreen } from "../session/sessionMode/SessionModeScreen";
import { PrepareSession } from "../session/prepareSession/PrepareSession";
import { AuthenticationLayout } from "../layout/auth/AuthenticationLayout";
import { ResetPassword, SetPassword } from "../auth/SetPassword";
import { Forgot } from "../auth/Forgot";
import { EditSessionScreen } from "../session/components/EditSessionPage";
import { StoriesScreen } from "../curation/stories/screens/StoriesScreen";
import { CollectionsScreen } from "../curation/collections/screens/CollectionsScreen";
import { CurriculumsScreen } from "../curation/curriculums/CurriculumsScreen";
import { ThemesScreen } from "../curation/themes/ThemesScreen";
import { BranchedStoriesScreen } from "../curation/branched-stories/screens/BranchedStoriesScreen";
import { BranchedStoryDetailsScreen } from "../curation/branched-stories/screens/BranchedStoryDetailsScreen";
import { BranchedStoryStructureScreen } from "../curation/branched-stories/screens/BranchedStoryStructureScreen";
import { CreateBranchedStoryWizard } from "../curation/branched-stories/wizard/CreateBranchedStoryWizard";
import { EditBranchedStoryWizard } from "../curation/branched-stories/wizard/EditBranchedStoryWizard";
import { StatsProvider } from "../stats/stats";
import { MapStory } from "../curation/stories/map/MapStory";
import { FetchProvider } from "../../core/components/crud/contexts/fetch.context";
import { getMapped } from "../../client/api/story";
import { ViewCollection } from "../home/ViewCollection";
import { ViewCollectionStory } from "../home/ViewCollectionStory";
import { ViewCollectionTheme } from "../home/ViewCollectionTheme";
import { ViewCollectionTopic } from "../home/ViewCollectionTopic";
import { ViewCurriculum } from "../home/ViewCurriculum";
//import { HomeScreen } from "../home/HomeScreen";
import { ViewOnMobile } from "../layout/ViewOnMobile";
import { AddUserScreen } from "../users/AddUserScreen";
import { ClassroomScreen } from "../classroom/ClassroomScreen";
import { useAuth } from "../../contexts/auth.context";
import { ClassSessionScreen } from "../classroom/session/ClassSessionScreen";
import { ExploreScreen } from "../home/ExploreScreen";
import SubscriptionSuccess from "../organisation/onboarding/SubscriptionSuccess";
import { PublicLayout } from "../layout/public/PublicLayout";
import { MyParticipants } from "../participant/MyParticipants";
import { ViewParticipant } from "../participant/ViewParticipant";
import { DeliverSessionOverview } from "../session/deliverSession/DeliverSessionOverview";
import { PreparePage } from "../session/prepareSession/PreparePage";
import { DeliverPage } from "../session/deliverSession/DeliverPage";
import { AssessHub } from "../assess/AssessHub";
import { GroupLivePage } from "../assess/groupLive/GroupLivePage";
import { GroupLiveJoin } from "../assess/groupLive/GroupLiveJoin";
import { GroupProxyPage } from "../assess/groupProxy/GroupProxyPage";
import { AssessmentAiRoute } from "../assess/assessmentAi/AssessmentAiRoute";
import { ImpactScoresMockPage } from "../assess/impact/ImpactScoresMockPage";
import SurveyBuilderContainer from "../assess/builder/SurveyBuilderContainer";
import { SurveyResultsPage } from "../assess/surveyResults/SurveyResultsPage";
import { SurveyResultDetailPage } from "../assess/surveyResults/SurveyResultDetailPage";
import { AssessFlowProvider } from "../assess/flow/AssessFlowContext";
import { NewAssessmentChooser } from "../assess/flow/NewAssessmentChooser";
import { AssessmentDetail } from "../assess/flow/AssessmentDetail";
import { TakeAssessment } from "../assess/flow/TakeAssessment";
import { AssessConversation } from "../assess/flow/AssessConversation";
import { BaselineReport } from "../assess/flow/BaselineReport";
import { ProgrammeReportPicker } from "../assess/report/ProgrammeReportPicker";
import { ProgrammeReportPage } from "../assess/report/ProgrammeReportPage";
import {
    isAssessPreviewEnabled,
    isDesignPreviewEnabled,
    isSurveyBuilderOpenToAllOrgs,
} from "../../config/featureFlags";
import { CreateProgrammeStepperPreview } from "../programme/preview/CreateProgrammeStepperPreview";
import { CollectionOverviewPreviewPage } from "../programme/preview/CollectionOverviewPreviewPage";
import { CREATE_PROGRAMME_PREVIEW_PATH } from "../programme/preview/previewRoutes";

/** Build-time constant (VER-112) — read once so every gated route agrees. */
const assessPreviewEnabled = isAssessPreviewEnabled();

/** Build-time constant (VER-128) — read once so every gated route agrees. */
const designPreviewEnabled = isDesignPreviewEnabled();

/** Wraps ONLY the multi-step assess flow (new → take → conversation → report) so the shared
 *  wizard state (population / scale / score) spans exactly those screens — the provider used to
 *  wrap the entire admin router, mounting flow state on every unrelated page. */
const AssessFlowLayoutRoute = (): React.ReactElement => (
    <AssessFlowProvider>
        <Outlet />
    </AssessFlowProvider>
);

const Routes = (): React.ReactElement => {
    const { user } = useAuth();
    return (
        <>
            <VAlert />
            <RouterRoutes>
                {process.env.NODE_ENV === "development" && (
                    <Route
                        path="/component-library"
                        element={<BaseRoute layout={LayoutWithNav} component={ComponentLibrary} />}
                    />
                )}
                <Route
                    path="/subscription/success"
                    element={<BaseRoute layout={PublicLayout} component={SubscriptionSuccess} />}
                />
                <Route
                    path="/explore"
                    element={<BaseRoute layout={LayoutWithNav} component={ExploreScreen} permission="DashboardView" />}
                />
                <Route
                    path="/explore/curriculum/:id"
                    element={<BaseRoute layout={LayoutWithNav} component={ViewCurriculum} permission="DashboardView" />}
                />
                <Route
                    path="/explore/curriculum/:id/collection/:collectionId"
                    element={<BaseRoute layout={LayoutWithNav} component={ViewCollection} permission="DashboardView" />}
                />
                <Route
                    path="/explore/collection/:id/story/:storyId"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ViewCollectionStory} permission="DashboardView" />
                    }
                />
                <Route
                    path="/explore/collection/:id/theme/:themeId"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ViewCollectionTheme} permission="DashboardView" />
                    }
                />
                <Route
                    path="/explore/collection/:id/topic/:topicId"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ViewCollectionTopic} permission="DashboardView" />
                    }
                />
                <Route
                    path="/prepare-session/:id"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={PrepareSession} permission="ProgrammeViewAll" />
                    }
                />
                <Route
                    path="/deliver-session/:id"
                    element={
                        <BaseRoute
                            layout={LayoutWithNav}
                            component={DeliverSessionOverview}
                            permission="ProgrammeViewAll"
                        />
                    }
                />
                <Route
                    path="/prepare"
                    element={<BaseRoute layout={LayoutWithNav} component={PreparePage} permission="ProgrammeViewAll" />}
                />
                <Route
                    path="/deliver"
                    element={<BaseRoute layout={LayoutWithNav} component={DeliverPage} permission="ProgrammeViewAll" />}
                />
                <Route
                    path="/participants"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={MyParticipants} permission="ProgrammeViewAll" />
                    }
                />
                <Route
                    path="/participants/:id"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ViewParticipant} permission="ProgrammeViewAll" />
                    }
                />
                <Route
                    path="/programmes/*"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ProgrammesScreen} permission="ProgrammeViewAll" />
                    }
                />
                <Route
                    path="/home"
                    element={<BaseRoute layout={LayoutWithNav} component={Dashboard} permission="DashboardView" />}
                />
                {/* Assess hub + Phase 3 (VER-51) assessment surfaces. Specific routes precede the
                    /assess/* catch-all so they match first. Permits mirror the backend survey codes
                    (SurveyViewAll to view/list, SurveyView for detail, SurveyReportView for reports) —
                    seeded to Facilitator (read + report) and ProgrammeCoordinator (also authoring).

                    VER-112: the routes below render MOCK wellbeing figures and are gated behind
                    REACT_APP_ASSESS_PREVIEW (on locally + on staging, OFF in production). A permit
                    is not enough here — a Facilitator legitimately holds SurveyViewAll, so without
                    the flag they would see invented assessment data in production and could
                    reasonably read it as clinical fact. When a surface is wired to real data
                    (VER-119 hub, VER-120 results, VER-115 group proxy) it leaves this block.

                    denySchool: Phase 3 is not finished for school accounts. School Admin is
                    FullAccess, so hiding the nav item is not enough — the URL must 403 too. */}
                {/* VER-115: the group proxy read persists to native SurveyResponse and its history
                    comes from the API, so it is a real surface and is registered unconditionally.
                    SurveyViewAll gates viewing; recording is separately gated server-side by
                    SurveyProxyRespond, and the page hides the record button without it. */}
                <Route
                    path="/assess/group-proxy"
                    element={
                        <BaseRoute
                            layout={LayoutWithNav}
                            component={GroupProxyPage}
                            permission="SurveyViewAll"
                            denySchool
                        />
                    }
                />
                {assessPreviewEnabled ? (
                    <>
                        <Route
                            path="/assess/group-live/:assessmentId"
                            element={
                                <BaseRoute
                                    layout={LayoutWithNav}
                                    component={GroupLivePage}
                                    permission="SurveyViewAll"
                                    denySchool
                                />
                            }
                        />
                        <Route
                            path="/assess/ai/:assessmentId"
                            element={
                                <BaseRoute
                                    layout={LayoutWithNav}
                                    component={AssessmentAiRoute}
                                    permission="SurveyViewAll"
                                    denySchool
                                />
                            }
                        />
                        <Route
                            path="/assess/impact-preview"
                            element={
                                <BaseRoute
                                    layout={LayoutWithNav}
                                    component={ImpactScoresMockPage}
                                    permission="SurveyReportView"
                                    denySchool
                                />
                            }
                        />
                    </>
                ) : null}
                {/* TODO [VER-116]: the participant join screen is shown here as an authenticated
                    preview. The real participant-facing /join is a PUBLIC route (no auth) and
                    needs PrivateRoute/public-layout + security review before wiring. */}
                {assessPreviewEnabled ? (
                    <Route
                        path="/assess/join-preview"
                        element={
                            <BaseRoute
                                layout={LayoutWithNav}
                                component={GroupLiveJoin}
                                permission="SurveyViewAll"
                                denySchool
                            />
                        }
                    />
                ) : null}
                {/* VER-108 native survey builder — its own top-level /survey section (NOT under
                    /assess), now wired to the real /survey-templates API via SurveyBuilderContainer.
                    Gated on SurveyViewAll (the list is the entry point). Authoring (create/update/
                    delete) is enforced server-side, so read-only Facilitators can browse but their
                    save/publish calls 403 — full authoring stays with ProgrammeCoordinator/Admin.
                    verbalOnly: the builder authors the survey instruments Verbal ships to partner
                    organisations, so it is internal Verbal tooling. Partner orgs see neither the nav
                    entry nor the route — typing /survey/builder lands on /403, not the builder. */}
                <Route
                    path="/survey/builder"
                    element={
                        <BaseRoute
                            layout={LayoutWithNav}
                            component={SurveyBuilderContainer}
                            permission="SurveyViewAll"
                            /* TEMPORARY staging override — isSurveyBuilderOpenToAllOrgs(). Hiding
                               the nav entry is not enough: the path stays typeable, so the route
                               guard has to yield to the same switch, or the entry we just un-hid
                               would lead straight to /403 on the environment it was opened for. */
                            verbalOnly={!isSurveyBuilderOpenToAllOrgs()}
                        />
                    }
                />
                {/* VER-109 Survey Results — completed-survey response analytics (mock, frontend-only).
                    The static /survey/results ranks above the participant /survey/:id (see the
                    participant Routes passthrough); /survey/results/:resultId is 3-segment so it
                    never collides. Gated by VER-112 until VER-120 wires it to real response data. */}
                {assessPreviewEnabled ? (
                    <>
                        <Route
                            path="/survey/results"
                            element={
                                <BaseRoute
                                    layout={LayoutWithNav}
                                    component={SurveyResultsPage}
                                    permission="SurveyViewAll"
                                />
                            }
                        />
                        <Route
                            path="/survey/results/:resultId"
                            element={
                                <BaseRoute
                                    layout={LayoutWithNav}
                                    component={SurveyResultDetailPage}
                                    permission="SurveyView"
                                />
                            }
                        />
                    </>
                ) : null}
                {/* VER-128 design preview — "Verbal Prepare Refresh" canvas: the Create Programme
                    stepper refresh and the collection-overview visual refresh. Mock copy only, no
                    schema change (see the ticket's "Out of scope" list) — population group,
                    context notes, case-note upload and collection sectoral/keyword metadata have
                    no backing field yet. Gated the same fail-closed way as the assess previews.

                    Reached by URL only — nothing in the app links here. A follow-up wired the
                    "Create programme" nav entry and the My Programmes "Add New" button to the
                    stepper whenever this flag was on, which on staging left no way to create a
                    real programme at all, since the stepper persists nothing. Both entry points
                    are back on /programmes/add for every build; anyone reviewing the design opens
                    the path below directly. */}
                {/* Both surfaces gate on ProgrammeCreate, the permit for the journey they belong to
                    — entering programme creation and reviewing the collection it would use. The
                    collection preview asked for StoryCollectionView until VER-128 follow-up, which
                    sent a ProgrammeCoordinator holding ProgrammeCreate to /403 on the second of the
                    two review links. It renders MOCK_COLLECTION and issues no request, so there is
                    no collection data behind the narrower permit to protect. */}
                {designPreviewEnabled ? (
                    <>
                        <Route
                            path={CREATE_PROGRAMME_PREVIEW_PATH}
                            element={
                                <BaseRoute
                                    layout={LayoutWithNav}
                                    component={CreateProgrammeStepperPreview}
                                    permission="ProgrammeCreate"
                                />
                            }
                        />
                        <Route
                            path="/preview/collection/:collectionId"
                            element={
                                <BaseRoute
                                    layout={LayoutWithNav}
                                    component={CollectionOverviewPreviewPage}
                                    permission="ProgrammeCreate"
                                />
                            }
                        />
                    </>
                ) : null}
                {/* VER-108 / Phase 3 native assessment flow (new → take → conversation → report).
                    These four SHARE one AssessFlowProvider (the wizard's population/scale/score) via
                    the pathless layout route below — the provider no longer wraps the whole admin app.
                    RR v6 ranks these static/deeper paths above /assess/:id and the /assess/* hub.
                    Gated on VIEW permits: the flow is read/interact-only today. Response CAPTURE
                    (POST /survey-responses) isn't wired yet (W3); its write-permit is decided then. */}
                <Route element={<AssessFlowLayoutRoute />}>
                    <Route
                        path="/assess/new"
                        element={
                            <BaseRoute
                                layout={LayoutWithNav}
                                component={NewAssessmentChooser}
                                permission="SurveyViewAll"
                                denySchool
                            />
                        }
                    />
                    <Route
                        path="/assess/take/:scaleId"
                        element={
                            <BaseRoute
                                layout={LayoutWithNav}
                                component={TakeAssessment}
                                permission="SurveyView"
                                denySchool
                            />
                        }
                    />
                    <Route
                        path="/assess/conversation"
                        element={
                            <BaseRoute
                                layout={LayoutWithNav}
                                component={AssessConversation}
                                permission="SurveyView"
                                denySchool
                            />
                        }
                    />
                    <Route
                        path="/assess/report"
                        element={
                            <BaseRoute
                                layout={LayoutWithNav}
                                component={BaselineReport}
                                permission="SurveyView"
                                denySchool
                            />
                        }
                    />
                </Route>
                {/* VER-109 automated per-programme outcome reporting (native survey builder).
                    Picker (/assess/reports) → report (/assess/report/:programmeId). Both back real
                    endpoints (survey-report), gated on SurveyReportView (also enforced server-side).
                    RR v6 ranks the static picker and 3-segment report path above /assess/:id + hub. */}
                <Route
                    path="/assess/reports"
                    element={
                        <BaseRoute
                            layout={LayoutWithNav}
                            component={ProgrammeReportPicker}
                            permission="SurveyReportView"
                            denySchool
                        />
                    }
                />
                <Route
                    path="/assess/report/:programmeId"
                    element={
                        <BaseRoute
                            layout={LayoutWithNav}
                            component={ProgrammeReportPage}
                            permission="SurveyReportView"
                            denySchool
                        />
                    }
                />
                {/* :id detail — must follow the static /assess/* siblings above.
                    RR v6 ranking keeps /assess/new etc. ahead of this dynamic segment. */}
                <Route
                    path="/assess/:id"
                    element={
                        <BaseRoute
                            layout={LayoutWithNav}
                            component={AssessmentDetail}
                            permission="SurveyView"
                            denySchool
                        />
                    }
                />
                <Route
                    path="/assess/*"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={AssessHub} permission="SurveyViewAll" denySchool />
                    }
                />
                <Route
                    path="/add-users"
                    element={
                        <BaseRoute
                            layout={LayoutWithNav}
                            component={AddUserScreen}
                            permission="FacilitatorViewAll"
                            denyDemo
                        />
                    }
                />
                <Route
                    path="/users/*"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={UsersScreen} permission="SettingsView" denyDemo />
                    }
                />
                <Route path="/403" element={<BaseRoute layout={LayoutWithNav} component={PermissionDenied} />} />
                <Route path="/login" element={<BaseRoute layout={AuthenticationLayout} component={Login} />} />
                <Route
                    path="/create-account/:token"
                    element={<BaseRoute layout={AuthenticationLayout} component={SetPassword} />}
                />
                <Route path="/forgot" element={<BaseRoute layout={AuthenticationLayout} component={Forgot} />} />
                <Route
                    path="/reset/:token"
                    element={<BaseRoute layout={AuthenticationLayout} component={ResetPassword} />}
                />
                <Route
                    path="/organisation/signup"
                    element={<BaseRoute layout={AuthenticationLayout} component={CreateOrganisationPage} />}
                />
                <Route
                    path="/start-session/:id"
                    element={
                        <BaseRoute
                            layout={LayoutWithoutNav}
                            component={SessionModeScreen}
                            permission="ProgrammeViewAll"
                        />
                    }
                />
                <Route
                    path="/classrooms/start-session/:id"
                    element={
                        <BaseRoute
                            layout={LayoutWithoutNav}
                            component={ClassSessionScreen}
                            permission={"ClassroomView"}
                        />
                    }
                />
                <Route
                    path="/prepare-session/:id"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={PrepareSession} permission="ProgrammeViewAll" />
                    }
                />
                <Route
                    path="/sessions/edit/:id"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={EditSessionScreen} permission="SessionUpdate" />
                    }
                />
                <Route
                    path="/stories/*"
                    element={<BaseRoute component={StoriesScreen} layout={LayoutWithNav} permission="StoryViewAll" />}
                />

                {/* Branched Stories Routes */}
                <Route
                    path="/curation/branched-stories/*"
                    element={
                        <BaseRoute component={BranchedStoriesScreen} layout={LayoutWithNav} permission="StoryViewAll" />
                    }
                />
                <Route
                    path="/curation/branched-stories/create"
                    element={
                        <BaseRoute
                            component={CreateBranchedStoryWizard}
                            layout={LayoutWithNav}
                            permission="StoryCreate"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/add"
                    element={
                        <BaseRoute
                            component={CreateBranchedStoryWizard}
                            layout={LayoutWithNav}
                            permission="StoryCreate"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/:id/details"
                    element={
                        <BaseRoute
                            component={BranchedStoryDetailsScreen}
                            layout={LayoutWithNav}
                            permission="StoryView"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/:id/structure"
                    element={
                        <BaseRoute
                            component={BranchedStoryStructureScreen}
                            layout={LayoutWithNav}
                            permission="StoryView"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/view/:id"
                    element={
                        <BaseRoute
                            component={BranchedStoryDetailsScreen}
                            layout={LayoutWithNav}
                            permission="StoryView"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/edit/:id"
                    element={
                        <BaseRoute
                            component={EditBranchedStoryWizard}
                            layout={LayoutWithNav}
                            permission="StoryUpdate"
                        />
                    }
                />

                <Route
                    path="/collections/*"
                    element={
                        <BaseRoute
                            component={CollectionsScreen}
                            layout={LayoutWithNav}
                            permission="StoryCollectionView"
                        />
                    }
                />
                <Route
                    path="/curriculums/*"
                    element={
                        <BaseRoute
                            component={CurriculumsScreen}
                            layout={LayoutWithNav}
                            permission="StoryCollectionCreate"
                        />
                    }
                />
                <Route
                    path="/themes/*"
                    element={
                        <BaseRoute component={ThemesScreen} layout={LayoutWithNav} permission="StoryCollectionCreate" />
                    }
                />
                <Route
                    path="/detect-mobile"
                    element={<BaseRoute layout={LayoutWithoutNav} component={ViewOnMobile} />}
                />
                <Route
                    path="/classrooms/*"
                    element={
                        <BaseRoute
                            component={ClassroomScreen}
                            layout={LayoutWithNav}
                            permission={user?.isSchool ? "ClassroomView" : undefined}
                        />
                    }
                />
                <Route
                    path="/stats/*"
                    element={<BaseRoute component={StatsProvider} layout={LayoutWithNav} permission="StatsViewAll" />}
                />
                {/** Keep this route last as routes after the fetch provider don't seem to work**/}
                <Route
                    path="/map/story/:id"
                    element={
                        <FetchProvider noun="Story" getItem={getMapped}>
                            <BaseRoute component={MapStory} layout={LayoutWithNav} permission="StoryCollectionView" />
                        </FetchProvider>
                    }
                />
            </RouterRoutes>
        </>
    );
};

export default Routes;
